import React, { useEffect, useState } from "react";
import {
  Plug,
  CheckCircle,
  XCircle,
  MessageCircle,
  Trello,
  CreditCard,
  Link2,
  Settings,
} from "lucide-react";
import { hubSpotService } from "../../services/HubSpotService";
import { stripeIntegrationService } from "../../services/StripeIntegrationService";
import { useToastContext } from "../../context/ToastContext";
import ConfirmDisconnectModal from "../hubspot/ConfirmDisconnectModal";
import { useLoader } from "../../context/LoaderContext";
import { formatDate } from "../../utils/DateUtils";
import FieldMappingModal from "../modals/FieldMappingModal";

interface ConnectionStatus {
  connected: boolean;
  loading: boolean;
  createdAt?: string;
  comingSoon?: boolean;
}

const ThirdPartyConnections: React.FC = () => {
  const [connections, setConnections] = useState<{
    hubspot: ConnectionStatus;
    stripe: ConnectionStatus;
    slack: ConnectionStatus;
    asana: ConnectionStatus;
  }>({
    hubspot: { connected: false, loading: true },
    stripe: { connected: false, loading: true },
    slack: { connected: false, loading: false, comingSoon: true },
    asana: { connected: false, loading: false, comingSoon: true },
  });

  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(
    null
  );
  const [isFieldMappingModalOpen, setIsFieldMappingModalOpen] = useState(false);

  const { showToast } = useToastContext();
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    const checkConnectionStatuses = async () => {
      try {
        const [hubspotResult, stripeResult] = await Promise.allSettled([
          hubSpotService.getHubSpotStatus(),
          stripeIntegrationService.getStripeStatus(),
        ]);

        const hubspotStatus =
          hubspotResult.status === "fulfilled"
            ? hubspotResult.value
            : { data: { connected: false }, message: "Not connected" };
        const stripeStatus =
          stripeResult.status === "fulfilled"
            ? stripeResult.value
            : { data: { connected: false }, message: "Not connected" };

        setConnections((prev) => ({
          ...prev,
          hubspot: {
            connected: hubspotStatus.data?.connected ?? false,
            loading: false,
            createdAt: hubspotStatus.data?.createdAt,
          },
          stripe: {
            connected: stripeStatus.data?.connected ?? false,
            loading: false,
            createdAt: stripeStatus.data?.createdAt,
          },
        }));
      } catch (error: any) {
        console.error("Failed to fetch connection statuses:", error);
        setConnections((prev) => ({
          ...prev,
          hubspot: { connected: false, loading: false },
          stripe: { connected: false, loading: false },
        }));
      }
    };

    checkConnectionStatuses();
  }, [showToast]);

  const getIntegrationIcon = (integration: string) => {
    switch (integration) {
      case "hubspot":
        return (
          <div className="w-10 h-10 rounded-lg bg-[#FF7A59] flex items-center justify-center flex-shrink-0">
            <Plug className="w-5 h-5 text-white" />
          </div>
        );
      case "stripe":
        return (
          <div className="w-10 h-10 rounded-lg bg-[#635BFF] flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-5 h-5 text-white" />
          </div>
        );
      default:
        return (
          <div className="w-10 h-10 rounded-lg bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
            <Plug className="w-5 h-5 text-[#F57E0C]" />
          </div>
        );
    }
  };

  const getIntegrationCategory = (integration: string) => {
    switch (integration) {
      case "hubspot":
        return "CRM";
      case "stripe":
        return "Billing";
      default:
        return "Integration";
    }
  };

  const getIntegrationDescription = (integration: string) => {
    switch (integration) {
      case "hubspot":
        return "Sync your CRM data, deals, contacts, and companies for real-time insights and automated workflows.";
      case "stripe":
        return "Connect your billing platform to track subscriptions, payments, and revenue data seamlessly.";
      default:
        return "Connect your tools to sync data and automate workflows.";
    }
  };

  const handleConnect = async (integration: string) => {
    switch (integration) {
      case "hubspot":
        try {
          const { data } = await hubSpotService.connectHubSpot();
          if (data.authorizationUrl) {
            window.location.href = data.authorizationUrl;
          }
        } catch (error) {
          showToast("error", "Failed to connect to HubSpot.");
        }
        break;
      case "stripe":
        try {
          const { data } = await stripeIntegrationService.connectStripe();
          if (data.authorizationUrl) {
            window.location.href = data.authorizationUrl;
          }
        } catch (error) {
          showToast("error", "Failed to connect to Stripe.");
        }
        break;
      case "slack":
      case "asana":
        // Coming soon - show a fun message
        showToast("info", "This integration is coming soon! 🚀");
        break;
    }
  };

  const handleDisconnect = (integration: string) => {
    setSelectedIntegration(integration);
    setShowDisconnectModal(true);
  };

  const confirmDisconnect = async () => {
    setShowDisconnectModal(false);
    showLoader();

    try {
      if (selectedIntegration === "hubspot") {
        await hubSpotService.disconnectHubSpot();
        setConnections((prev) => ({
          ...prev,
          hubspot: { connected: false, loading: false },
        }));
        showToast("success", "HubSpot disconnected successfully.");
      } else if (selectedIntegration === "stripe") {
        await stripeIntegrationService.disconnectStripe();
        setConnections((prev) => ({
          ...prev,
          stripe: { connected: false, loading: false },
        }));
        showToast("success", "Stripe disconnected successfully.");
      }
      
      // Dispatch custom event to notify other components (like Home page)
      window.dispatchEvent(new CustomEvent("connectionStatusChanged"));
    } catch (error) {
      showToast("error", `Failed to disconnect from ${selectedIntegration}.`);
    } finally {
      hideLoader();
      setSelectedIntegration(null);
    }
  };


  // Filter out coming soon connectors for the main display
  const activeConnectors = Object.entries(connections).filter(
    ([_, value]) => !value.comingSoon
  );

  const connectedCount = activeConnectors.filter(
    ([_, value]) => value.connected
  ).length;

  return (
    <div className="w-full flex flex-col">
      <div className="bg-background-default dark:bg-background-dark">
        <div className="px-6 py-6">
          <h1 className="text-2xl font-semibold text-black dark:text-text-primaryDark mb-1">
            Connectors
          </h1>
          <p className="text-sm text-black/60 dark:text-text-secondaryDark">
            Connect your tools to sync data and automate workflows
          </p>
        </div>
      </div>

      <div className="flex-1 px-6 pb-6">
        {/* Apollo-style Connectors List */}
        <div className="bg-[#0F0F0F] rounded-lg border border-[#2A2A2A] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#2A2A2A]">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-white">
                Connected integrations
              </h2>
              <span className="px-2 py-0.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded text-xs font-medium text-[#8B8B8B]">
                {connectedCount}
              </span>
            </div>
          </div>

          {/* Column Headers */}
          <div className="px-6 py-3 border-b border-[#2A2A2A] bg-[#0F0F0F]">
            <div className="grid grid-cols-[1fr_auto] gap-4">
              <div className="text-xs font-semibold text-[#8B8B8B] uppercase tracking-wider">
                Application
              </div>
              <div className="text-xs font-semibold text-[#8B8B8B] uppercase tracking-wider text-right">
                Action
              </div>
            </div>
          </div>

          {/* Connector Rows */}
          <div className="divide-y divide-[#2A2A2A]">
            {activeConnectors.map(([key, value]) => {
              const integrationName = key === "hubspot" ? "HubSpot" : "Stripe";
              const category = getIntegrationCategory(key);
              
              return (
                <div
                  key={key}
                  className="px-6 py-4 hover:bg-[#1A1A1A]/50 transition-colors"
                >
                  <div className="grid grid-cols-[1fr_auto] gap-4 items-start">
                    {/* Left Side: Logo, Name, Category, Description */}
                    <div className="flex items-start gap-4">
                      {getIntegrationIcon(key)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <h3 className="text-base font-semibold text-white">
                            {integrationName}
                          </h3>
                          <span className="px-2 py-0.5 bg-blue-600/20 border border-blue-500/30 rounded text-xs font-medium text-blue-400">
                            {category}
                          </span>
                          {value.connected && (
                            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-green-500/20 border border-green-500/30 rounded">
                              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                              <span className="text-xs font-medium text-green-400">
                                Connected
                              </span>
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-[#8B8B8B] leading-relaxed">
                          {getIntegrationDescription(key)}
                        </p>
                        {value.connected && value.createdAt && (
                          <p className="text-xs text-[#6B6B6B] mt-2">
                            Connected {formatDate(value.createdAt)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Right Side: Action Button */}
                    <div className="flex items-center gap-2">
                      {key === "hubspot" && value.connected && (
                        <button
                          onClick={() => setIsFieldMappingModalOpen(true)}
                          className="p-2 rounded-lg hover:bg-[#1A1A1A] transition-colors"
                          title="Field Mappings"
                          aria-label="Open Field Mappings"
                        >
                          <Settings className="w-4 h-4 text-[#8B8B8B] hover:text-[#F57E0C] transition-colors" />
                        </button>
                      )}
                      {value.connected ? (
                        <button
                          onClick={() => handleDisconnect(key)}
                          className="px-4 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-sm font-medium text-white hover:bg-[#141414] hover:border-[#3A3A3A] transition-colors"
                        >
                          Disconnect
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConnect(key)}
                          className="px-4 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-sm font-medium text-white hover:bg-[#141414] hover:border-[#3A3A3A] transition-colors"
                        >
                          Connect
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <ConfirmDisconnectModal
          open={showDisconnectModal}
          onClose={() => setShowDisconnectModal(false)}
          onConfirm={confirmDisconnect}
          integration={selectedIntegration || ""}
        />

        <FieldMappingModal
          isOpen={isFieldMappingModalOpen}
          onClose={() => setIsFieldMappingModalOpen(false)}
        />
      </div>
    </div>
  );
};

export default ThirdPartyConnections;
